/**
 * The AI board's shared chrome: the section lead-in that introduces a group of
 * panels, the outlined card one panel renders in, and the two honesty affordances
 * every panel on this board needs — a caption that states truncation, and a
 * "measured zero" footnote.
 *
 * `PanelCard` is the non-chart twin of `@/components/board`'s `ChartCard`: same
 * outlined card, same title/caption header, no table twin because the content
 * already *is* a table.
 */

import { Icon } from "@norican/icons";
import { Badge, Card, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { type Reading } from "./status";

interface PanelCardProps {
  /** Panel heading. */
  title: string;
  /** Muted line beside the heading — a count, a window, a coverage caveat. */
  caption?: ReactNode;
  /** Extra header controls, e.g. a scope switcher or a "View all N →" link. */
  actions?: ReactNode;
  /** Panel content. */
  children: ReactNode;
  /**
   * Body padding. `none` for panels whose content is a full-bleed `DataGrid`,
   * which brings its own.
   */
  padding?: "none" | "sm" | "md" | "lg";
  /** Optional left accent, for a panel that is itself a finding. */
  accentTone?: "neutral" | "accent" | "success" | "danger" | "warning";
}

export function PanelCard({
  title,
  caption,
  actions,
  children,
  padding = "md",
  accentTone,
}: PanelCardProps) {
  return (
    <Card variant="outline" accent={accentTone ? "left" : undefined} accentTone={accentTone}>
      <Card.Header>
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <Typography variant="heading-4" as="h2">
            {title}
          </Typography>
          <div className="flex flex-wrap items-center gap-3">
            {caption && (
              <Typography variant="body-sm" tone="muted">
                {caption}
              </Typography>
            )}
            {actions}
          </div>
        </div>
      </Card.Header>
      <Card.Body padding={padding}>{children}</Card.Body>
    </Card>
  );
}

interface SectionHeadingProps {
  /** Section title — one of the board's answers. */
  title: string;
  /**
   * What this section does and does not claim.
   *
   * Carried here rather than left to the cards because a section's limits (a
   * count that is a floor, a rate computed over the accounts that *could* be
   * read, a zero that is bad news) are properties of the whole section, and
   * stating them once above the panels is what stops each card hedging on its own.
   */
  subtitle: string;
  /** Anchor id, so a KPI tile can link down to this section (§4.1). */
  id?: string;
}

/** A section lead-in, so the panels below it read as one answer rather than five. */
export function SectionHeading({ title, subtitle, id }: SectionHeadingProps) {
  return (
    <div className="flex scroll-mt-6 flex-col gap-1" id={id}>
      <Typography variant="heading-3" as="h2">
        {title}
      </Typography>
      <Typography variant="body-sm" tone="muted" as="p">
        {subtitle}
      </Typography>
    </div>
  );
}

interface ReadingCellProps {
  /** The decided reading — see `./status.ts`. Panels never re-decide. */
  reading: Reading;
  /**
   * Show the badge beside the value.
   *
   * On by default for the states that must not be mistaken for one another: a
   * measured zero and an unread value both need words, not just a colour (§3.2).
   * A plain positive value carries no badge, because "measured: 4 deployments"
   * adds nothing to "4".
   */
  showBadge?: boolean;
}

/**
 * One `Reading` rendered as a table cell.
 *
 * The component that makes `null` and `0` impossible to confuse in a grid: an
 * unread value is an em dash with a muted "not counted" badge, a measured zero is
 * a literal `0` with a toned badge naming why it matters, and neither can ever be
 * mistaken for the other at a glance or by a screen reader.
 */
export function ReadingCell({ reading, showBadge }: ReadingCellProps) {
  const withBadge = showBadge ?? (reading.unknown || reading.measuredZero);
  return (
    <span className="inline-flex items-center gap-2" title={reading.note}>
      <span className="tabular-nums">{reading.text}</span>
      {withBadge && (
        <Badge
          tone={reading.tone === "neutral" ? "neutral" : reading.tone}
          variant="outline"
          size="xs"
        >
          <span className="inline-flex items-center gap-1">
            <Icon icon={reading.icon} size="xs" />
            {reading.badge}
          </span>
        </Badge>
      )}
    </span>
  );
}
